import React, { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Code } from "lucide-react";

// Define a type for the function names we know exist in the database
type KnownFunctions = 'get_admin_analytics' | 'get_enhanced_analytics';

const AnalyticsDebugPanel = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [debugInfo, setDebugInfo] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [functionStatus, setFunctionStatus] = useState<Record<string, boolean>>({});

  const checkFunction = async (funcName: KnownFunctions) => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.rpc(funcName);
      setFunctionStatus(prev => ({
        ...prev,
        [funcName]: !error
      }));
      
      if (error) {
        console.error(`Error testing ${funcName}:`, error);
      }
      
      return !error;
    } catch (err) {
      console.error(`Exception testing ${funcName}:`, err);
      setFunctionStatus(prev => ({
        ...prev,
        [funcName]: false
      }));
      return false;
    }
  };

  const runDiagnostics = async () => {
    setIsLoading(true);
    setDebugInfo(null);
    
    try {
      // Test each analytics function
      const basicAnalyticsOk = await checkFunction('get_admin_analytics');
      const enhancedAnalyticsOk = await checkFunction('get_enhanced_analytics');
      
      // Get database version info using a raw query instead of a non-existent table
      const { data: versionData, error: versionError } = await supabase
        .rpc('get_admin_analytics'); // Use an existing function instead
      
      if (versionError) {
        console.error("Error getting database version:", versionError);
      }
      
      setDebugInfo({
        timestamp: new Date().toISOString(),
        functionsStatus: {
          get_admin_analytics: basicAnalyticsOk,
          get_enhanced_analytics: enhancedAnalyticsOk
        },
        dbInfo: versionError ? { error: versionError.message } : (versionData || { version: 'unknown' })
      });
    } catch (error) {
      console.error("Error running diagnostics:", error);
      setDebugInfo({ 
        error: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date().toISOString()
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="mt-8 border border-dashed border-gray-300 rounded-lg p-4">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <Button variant="outline" size="sm" className="flex items-center">
            <Code className="h-4 w-4 mr-2" />
            {isOpen ? "Hide Debug Tools" : "Show Debug Tools"}
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-4">
          <div className="space-y-4">
            <div className="flex space-x-2">
              <Button 
                variant="secondary" 
                size="sm"
                onClick={runDiagnostics}
                disabled={isLoading}
              >
                {isLoading ? "Running..." : "Run Analytics Diagnostics"}
              </Button>
            </div>
            
            {debugInfo && (
              <div className="mt-4">
                <h4 className="text-sm font-medium mb-2">Diagnostics Results:</h4>
                <div className="bg-gray-50 rounded p-3 text-xs font-mono whitespace-pre-wrap">
                  {JSON.stringify(debugInfo, null, 2)}
                </div>
                
                {Object.entries(functionStatus).map(([funcName, isOk]) => (
                  <Alert key={funcName} variant={isOk ? "default" : "destructive"} className="mt-2">
                    <AlertDescription>
                      Function <code className="bg-gray-100 px-1 rounded">{funcName}</code>: 
                      {isOk ? " Working correctly" : " Has errors"}
                    </AlertDescription>
                  </Alert>
                ))}
              </div>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};

export default AnalyticsDebugPanel;
